import { SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Select, Spin } from 'antd';
import { debounce } from 'lodash';
import { FC, useMemo, useState } from 'react';
import { useSearchDatasetBriefs } from '../../../../../queries/useSearchDatasetBriefsQuery';
import useHomeStore from '../../../../../store/homeStore';
import { SearchResultOptionLabel } from './SearchResultOptionLabel';
import { useSelectedDataset } from './useSelectedDataset';

const popupClassName = css`
  .ant-select-item-group {
    color: rgba(0, 0, 0, 0.88);
    border-bottom: 1px solid #f0f0f0;
  }
`;

const SearchBar: FC = () => {
  const { onChange, datasetBriefs, selectedSearchId } = useSelectedDataset();

  const dropdownSearchbarOpen = useHomeStore((state) => state.dropdownSearchbarOpen);
  const setDropdownSearchbarOpen = useHomeStore((state) => state.setDropdownSearchbarOpen);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300), [setSearchTerm]);

  const { isSearching, isLoading, results } = useSearchDatasetBriefs(searchTerm);

  const resetSearchTerm = () => {
    debouncedSetSearchTerm.cancel();
    setSearchTerm('');
  };

  const handleChange = (id: string) => {
    resetSearchTerm();
    onChange(id);
  };

  const handleDropdownVisibleChange = (isOpen: boolean) => {
    if (!isOpen) resetSearchTerm();
    setDropdownSearchbarOpen(isOpen);
  };

  const topRelevanceScore = Math.max(0, ...results.map((data) => data.relevanceScore ?? 0));
  const bestMatches = results.filter((data) => (data.relevanceScore ?? 0) === topRelevanceScore);
  const otherResults = results.filter((data) => (data.relevanceScore ?? 0) !== topRelevanceScore);
  const isGroupedView = isSearching && results.length > 0;

  return (
    <Select
      id="Header-Search_Input"
      data-testid="Header-Search_Input"
      open={dropdownSearchbarOpen}
      placeholder="Search Datasets…"
      onDropdownVisibleChange={handleDropdownVisibleChange}
      showSearch
      value={selectedSearchId === '' ? undefined : selectedSearchId}
      style={{ width: '325px' }}
      popupMatchSelectWidth={480}
      classNames={{ popup: { root: popupClassName } }}
      optionLabelProp="label"
      onChange={handleChange}
      onSearch={debouncedSetSearchTerm}
      filterOption={false}
      loading={isSearching && isLoading}
      notFoundContent={isSearching && isLoading ? <Spin size="small" /> : undefined}
      suffixIcon={<SearchOutlined />}
    >
      {isGroupedView ? (
        <>
          <Select.OptGroup label="Best matches">
            {bestMatches.map((data) => (
              <Select.Option key={data.id} value={data.id} label={data.title}>
                <SearchResultOptionLabel
                  title={data.title}
                  description={data.description}
                  query={searchTerm}
                />
              </Select.Option>
            ))}
          </Select.OptGroup>
          {otherResults.length > 0 && (
            <Select.OptGroup label="Other results">
              {otherResults.map((data) => (
                <Select.Option key={data.id} value={data.id} label={data.title}>
                  <SearchResultOptionLabel
                    title={data.title}
                    description={data.description}
                    query={searchTerm}
                  />
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
        </>
      ) : (
        datasetBriefs.map((data) => (
          <Select.Option key={data.id} value={data.id} label={data.title}>
            <SearchResultOptionLabel title={data.title} />
          </Select.Option>
        ))
      )}
    </Select>
  );
};

export default SearchBar;
